import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { DotsSixVertical } from "phosphor-react";
import { Reorder } from "framer-motion";
import fieldTemplatesStyles from "../../Settings/FieldTemplatesSettings/FieldTemplatesStyles";
import { fget } from "../../../API/callsAPI";

export default function EditCustomPageColumnsTab({
  projectId,
  trackableId,
  trackableName,
  initialColumns = {},
  onSelectColumns,
}) {
  const classes = fieldTemplatesStyles();
  const [availableColumns, setAvailableColumns] = useState([]);
  const [customColumns, setCustomColumns] = useState([]);
  const [selectedDefaultNames, setSelectedDefaultNames] = useState(new Set());
  const [selectedCustomIdentifiers, setSelectedCustomIdentifiers] = useState(
    new Set()
  );
  const [orderedSelectedColumns, setOrderedSelectedColumns] = useState([]);

  const selectedColumns = useMemo(() => {
    if (orderedSelectedColumns.length > 0) {
      return orderedSelectedColumns;
    }
    const selectedDefault = availableColumns.filter((col) =>
      selectedDefaultNames.has(col.name)
    );
    const selectedCustom = customColumns.filter((col) =>
      selectedCustomIdentifiers.has(col.identifier)
    );
    return [...selectedDefault, ...selectedCustom];
  }, [
    availableColumns,
    customColumns,
    selectedDefaultNames,
    selectedCustomIdentifiers,
    orderedSelectedColumns,
  ]);

  useEffect(() => {
    if (trackableId) {
      handleFetchAvailableFields(trackableId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackableId]);

  const handleFetchAvailableFields = async (trackableId) => {
    try {
      const res = await fget({
        url: `trackables/trackable-field/?project=${projectId}&trackable=${trackableId}`,
      });

      const defaultFields = res?.data?.default_fields.map((field) => ({
        id: field.id,
        name: field.name,
        isRequired: field.required,
      }));

      setAvailableColumns(defaultFields);

      const customFields = res.data.custom_fields.map((field) => ({
        id: field.id,
        name: field.name,
        identifier: field.identifier,
        isRequired: false,
      }));

      setCustomColumns(customFields);

      // If initialColumns (page_config) exists, use it to pre-select columns
      if (initialColumns && Object.keys(initialColumns).length > 0) {
        // Convert page_config to ordered array: {0: "file", 1: "code"} => ["file", "code"]
        const orderedColumnNames = Object.entries(initialColumns)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([_, name]) => name);

        // Find matching columns from both default and custom fields
        const selectedDefaults = new Set();
        const selectedCustoms = new Set();
        const orderedCols = [];

        orderedColumnNames.forEach((colName) => {
          // Check in default fields
          const defaultField = defaultFields.find((f) => f.name === colName);
          if (defaultField) {
            selectedDefaults.add(colName);
            orderedCols.push(defaultField);
            return;
          }

          // Check in custom fields
          const customField = customFields.find(
            (f) => f.name === colName || f.identifier === colName
          );
          if (customField) {
            selectedCustoms.add(customField.identifier);
            orderedCols.push(customField);
          }
        });

        setSelectedDefaultNames(selectedDefaults);
        setSelectedCustomIdentifiers(selectedCustoms);
        setOrderedSelectedColumns(orderedCols);
      } else {
        // Fallback to only required fields if no initialColumns
        const requiredFields = defaultFields.filter((field) => field.isRequired);
        const requiredFieldNames = new Set(
          requiredFields.map((field) => field.name)
        );

        setSelectedDefaultNames(requiredFieldNames);
        setSelectedCustomIdentifiers(new Set());
        setOrderedSelectedColumns(requiredFields);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDefaultColumnToggle = useCallback(
    (name) => {
      setSelectedDefaultNames((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(name)) {
          newSet.delete(name);
        } else {
          newSet.add(name);
        }
        return newSet;
      });

      setOrderedSelectedColumns((prev) => {
        const column = availableColumns.find((col) => col.name === name);
        if (!column) return prev;

        const exists = prev.some((col) => col.name === name);
        if (exists) {
          return prev.filter((col) => col.name !== name);
        } else {
          return [...prev, column];
        }
      });
    },
    [availableColumns]
  );

  const handleCustomColumnToggle = useCallback(
    (identifier) => {
      setSelectedCustomIdentifiers((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(identifier)) {
          newSet.delete(identifier);
        } else {
          newSet.add(identifier);
        }
        return newSet;
      });

      setOrderedSelectedColumns((prev) => {
        const column = customColumns.find(
          (col) => col.identifier === identifier
        );
        if (!column) return prev;

        const exists = prev.some((col) => col.identifier === identifier);
        if (exists) {
          return prev.filter((col) => col.identifier !== identifier);
        } else {
          return [...prev, column];
        }
      });
    },
    [customColumns]
  );

  const handleReorder = useCallback((newOrder) => {
    setOrderedSelectedColumns(newOrder);
  }, []);

  useEffect(() => {
    if (onSelectColumns) {
      onSelectColumns(orderedSelectedColumns);
    }
  }, [orderedSelectedColumns, onSelectColumns]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Box>
        <Typography
          sx={{ mb: 1, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Trackable Type
        </Typography>
        <Box
          sx={{
            backgroundColor: "#252A38",
            borderRadius: "10px",
            p: 2,
            border: "1px solid #606479",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
            {trackableName}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: "rgba(255,255,255,0.6)",
              mt: 0.5,
            }}
          >
            Trackable type is fixed for this page
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{ mb: 2, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Available columns from this row type:
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: "12px",
          }}
        >
          {availableColumns.map((column) => (
            <FormControlLabel
              key={`default-${column.name}`}
              control={
                <Checkbox
                  checked={selectedDefaultNames.has(column.name)}
                  onChange={() => handleDefaultColumnToggle(column.name)}
                  sx={{
                    color: "#606479",
                    "&.Mui-checked": {
                      color: "#6C65D9",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Typography sx={{ fontSize: "14px", color: "white" }}>
                    {column.name}
                  </Typography>
                  {column.isRequired && (
                    <Typography sx={{ fontSize: "14px", color: "#FF6B6B" }}>
                      *
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                backgroundColor: "#252A38",
                borderRadius: "8px",
                padding: "8px 12px",
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{ mb: 2, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Custom columns:
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(5, 1fr)",
            },
            gap: "12px",
          }}
        >
          {customColumns.map((column) => (
            <FormControlLabel
              key={`custom-${column.identifier}`}
              control={
                <Checkbox
                  checked={selectedCustomIdentifiers.has(column.identifier)}
                  onChange={() => handleCustomColumnToggle(column.identifier)}
                  sx={{
                    color: "#606479",
                    "&.Mui-checked": {
                      color: "#6C65D9",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "14px", color: "white" }}>
                  {column.name}
                </Typography>
              }
              sx={{
                backgroundColor: "#252A38",
                borderRadius: "8px",
                padding: "8px 12px",
                margin: 0,
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Typography
          sx={{ mb: 2, fontSize: "14px", color: "rgba(255,255,255,0.7)" }}
        >
          Table preview:
        </Typography>
        {selectedColumns.length > 0 ? (
          <Box
            sx={{
              overflowX: "auto",
              overflowY: "visible",
              pb: 1,
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#606479",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#707489",
                },
              },
            }}
          >
            <Reorder.Group
              axis="x"
              values={selectedColumns}
              onReorder={handleReorder}
              role="list"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                paddingLeft: "0px",
                listStyle: "none",
                width: "max-content",
                minHeight: "60px",
              }}
            >
              {selectedColumns.map((column, index) => {
                const isCustomColumn = customColumns.some(
                  (cc) => cc.identifier === column.identifier
                );
                const uniqueKey = isCustomColumn
                  ? `custom-${column.identifier}`
                  : `default-${column.name}`;

                return (
                  <Reorder.Item
                    drag
                    key={uniqueKey}
                    value={column}
                    role="listitem"
                    style={{
                      cursor: "grab",
                      flexShrink: 0,
                      minWidth: "fit-content",
                    }}
                    whileDrag={{
                      scale: 1.05,
                      zIndex: 1000,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#252A38",
                        borderRadius: "8px",
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        minWidth: "120px",
                        border: "1px solid transparent",
                        "&:active": {
                          cursor: "grabbing",
                        },
                      }}
                    >
                      <DotsSixVertical
                        size={16}
                        color="#606479"
                        style={{ cursor: "grab" }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "white",
                            fontWeight: 500,
                          }}
                        >
                          {column.name}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#606479" }}>
                          {isCustomColumn ? "Custom Field" : "Default Field"}
                        </Typography>
                      </Box>
                    </Box>
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          </Box>
        ) : (
          <Typography sx={{ color: "#606479", fontSize: "14px" }}>
            Select columns to add them to preview
          </Typography>
        )}
      </Box>
    </Box>
  );
}
